import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Rating,
  Tooltip,
  Typography,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { RATING_CATEGORIES } from '../types';
import { averageRating } from '../helpers/ratingHelpers';
import { formatRelativeTime } from '../helpers/timeHelpers';
import type { AboutMeEntry } from '../helpers/mapAboutMeEntries';
import { FeedbackListPagination } from './FeedbackListPagination';

type AboutMeFeedbackTabProps = {
  entries: AboutMeEntry[];
  page: number;
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onPageChange: (page: number) => void;
};

export const AboutMeFeedbackTab: React.FC<AboutMeFeedbackTabProps> = ({
  entries,
  page,
  totalCount,
  isLoading,
  isFetching,
  isError,
  onPageChange,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Failed to load feedback about you.</Alert>;
  }

  if (entries.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
        <PersonIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
        <Typography variant="body2">No feedback about you yet.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', opacity: isFetching ? 0.6 : 1 }}>
      {isFetching && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            py: 2,
            zIndex: 1,
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
      <List disablePadding>
        {entries.map((entry, idx) => {
          const avg = averageRating(entry.ratings);
          return (
            <React.Fragment key={entry.id}>
              {idx > 0 && <Divider component="li" />}
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 1.5 }}>
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" component="span">
                        Anonymous feedback
                      </Typography>
                      <Tooltip title={`Avg: ${avg.toFixed(1)} / 5`} arrow>
                        <span>
                          <Rating
                            value={avg}
                            precision={0.5}
                            size="small"
                            readOnly
                            sx={{ fontSize: '0.9rem' }}
                          />
                        </span>
                      </Tooltip>
                      <Chip
                        label={formatRelativeTime(entry.submittedAt)}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                        {RATING_CATEGORIES.filter((cat) => (entry.ratings[cat.key] ?? 0) > 0).map(
                          (cat) => (
                            <Chip
                              key={cat.key}
                              label={`${cat.label}: ${entry.ratings[cat.key]}★`}
                              size="small"
                              sx={{ height: 20, fontSize: '0.68rem' }}
                              color="primary"
                              variant="outlined"
                            />
                          )
                        )}
                      </Box>
                      {entry.feedback && (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontStyle: 'italic',
                            fontSize: '0.8rem',
                            mt: 0.25,
                            display: 'block',
                          }}
                        >
                          "{entry.feedback}"
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
      <FeedbackListPagination page={page} totalCount={totalCount} onPageChange={onPageChange} />
    </Box>
  );
};
